import { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/apiEndPoints';
import defaultInstance from '../axiosHelper';

export default function useStaffData(initialTab = 'management') {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [apiData, setApiData] = useState({
    healthFacilityStaff: [],
    managementStaff: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    searchTerm: '',
    page: 0,
    rowsPerPage: 10,
    sortField: 'name',
    sortOrder: 'asc'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await defaultInstance.get(API_ENDPOINTS.STAFF_DATA);
        // Exclude the first row from healthFacilityStaff data
        const healthFacilityData = response.data.data.healthFacilityStaff || [];
        const filteredHealthFacilityData = healthFacilityData.length > 0 ? healthFacilityData.slice(1) : [];
        
        setApiData({
          healthFacilityStaff: filteredHealthFacilityData,
          managementStaff: response.data.data.managementStaff || []
        });
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (event) => {
    setFilters(prev => ({
      ...prev,
      searchTerm: event.target.value,
      page: 0
    }));
  };

  const handleChangePage = (event, newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setFilters(prev => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setFilters(prev => ({ ...prev, page: 0 }));
  };

  // Filter and sort data based on active tab and filters
  const filteredData = (apiData[`${activeTab}Staff`] || []).filter(item => {
    if (!item) return false;
    const searchLower = filters.searchTerm.toLowerCase();

    if (activeTab === 'healthFacility') {
      return (
        (item.health_facility?.toLowerCase() || '').includes(searchLower) ||
        (item.district?.toLowerCase() || '').includes(searchLower) ||
        (item.Asha?.toString() || '').toLowerCase().includes(searchLower) ||
        (item.ANM_CHO?.toString() || '').toLowerCase().includes(searchLower) ||
        (item.Field_Coordinator_Midori?.toString() || '').toLowerCase().includes(searchLower)
      );
    } else { // management
      return (
        (item.staff_name?.toLowerCase() || '').includes(searchLower) ||
        (item.designation?.toLowerCase() || '').includes(searchLower) ||
        (item.state_district?.toLowerCase() || '').includes(searchLower)
      );
    }
  });

  // Sort the filtered data
  const sortedData = [...filteredData].sort((a, b) => {
    const aValue = a[filters.sortField] || '';
    const bValue = b[filters.sortField] || '';

    if (aValue < bValue) {
      return filters.sortOrder === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return filters.sortOrder === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return {
    activeTab,
    data: sortedData,
    allData: apiData,
    loading,
    error,
    filters,
    handleSearch,
    handleChangePage,
    handleChangeRowsPerPage,
    handleTabChange,
    totalItems: filteredData.length,
    handleSort: (field) => {
      setFilters(prev => ({
        ...prev,
        sortField: field,
        sortOrder: prev.sortField === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
        page: 0
      }));
    }
  };
}
